import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

function Counter({ to, suffix = "", duration = 1800, testId }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref} data-testid={testId}>
      {val}
      {suffix}
    </span>
  );
}

export default function Results() {
  const stats = [
    { k: 20, suffix: "+", label: "Years in Nightlife" },
    { k: 1000, suffix: "+", label: "Events Delivered" },
    { k: 50, suffix: "+", label: "Venues Consulted" },
    { k: 100, suffix: "%", label: "Focused on Profit" },
  ];

  return (
    <section
      id="results"
      data-testid="results-section"
      className="relative py-20 md:py-40 bg-[#030303]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 md:mb-20 items-end">
          <div className="lg:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-5">
              Ch. 06 &mdash; Results
            </div>
            <h2 className="font-display text-white text-[34px] sm:text-4xl md:text-6xl lg:text-[80px] leading-[0.98] tracking-[-0.02em]">
              The receipts,{" "}
              <em className="font-editorial italic text-[#D4AF37] font-light">
                quietly stacked.
              </em>
            </h2>
          </div>
          <div className="lg:col-span-4 text-white/60 text-sm md:text-base leading-relaxed lg:pl-6">
            The metrics we care about aren't followers. They're covers, spend
            per head, and repeat rate.
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/8 rounded-2xl overflow-hidden border border-white/8">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className="bg-[#050505] p-6 sm:p-8 md:p-12 flex flex-col justify-between min-h-[180px] md:min-h-[220px]"
            >
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/45">
                0{i + 1}
              </div>
              <div>
                <div className="font-display text-white text-[40px] sm:text-5xl md:text-7xl leading-none tabular-nums">
                  <Counter to={s.k} suffix={s.suffix} testId={`stat-${i}`} />
                </div>
                <div className="mt-2 sm:mt-3 text-white/60 text-xs sm:text-sm md:text-base">
                  {s.label}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.24em] text-white/50">
          {["Hospitality Consultant", "Revenue Optimisation Expert", "DJ 20+ Years", "On-Ground Operator"].map(
            (t) => (
              <span
                key={t}
                className="px-3 py-1.5 rounded-full border border-white/12 bg-white/[0.02]"
              >
                {t}
              </span>
            ),
          )}
        </div>
      </div>
    </section>
  );
}
